export type UpdateFn = (fixedDtSec: number) => void;
export type RenderFn = (alpha: number, frameDtSec: number) => void;

export interface LoopOptions {
  fixedHz?: number;
  maxFrameSkipMs?: number;
}

export class GameLoop {
  private readonly fixedDtMs: number;
  private readonly fixedDtSec: number;
  private readonly maxFrameSkipMs: number;

  private rafId = 0;
  private running = false;
  private lastNow = 0;
  private accumulator = 0;

  constructor(
    private readonly update: UpdateFn,
    private readonly render: RenderFn,
    options: LoopOptions = {},
  ) {
    const hz = options.fixedHz ?? 60;
    this.fixedDtMs = 1000 / hz;
    this.fixedDtSec = 1 / hz;
    this.maxFrameSkipMs = options.maxFrameSkipMs ?? 100;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastNow = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.rafId !== 0) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  private readonly tick = (now: number): void => {
    if (!this.running) return;

    let frameMs = now - this.lastNow;
    if (frameMs > this.maxFrameSkipMs) frameMs = this.maxFrameSkipMs;
    if (frameMs < 0) frameMs = 0;
    this.lastNow = now;

    this.accumulator += frameMs;
    while (this.accumulator >= this.fixedDtMs) {
      this.update(this.fixedDtSec);
      this.accumulator -= this.fixedDtMs;
    }

    const alpha = this.accumulator / this.fixedDtMs;
    this.render(alpha, frameMs / 1000);

    this.rafId = requestAnimationFrame(this.tick);
  };
}
